import React, {useMemo} from "react";
import {NavLink, useParams, useLocation} from "react-router-dom";
import {NavHashLink} from "react-router-hash-link";
import {css} from "@emotion/css";
import {FlexColumn, styled, theme} from "@reside/ui";
import {RoleNameEnum} from "../../../utils/types/common";

import {AdminRoles} from "../../../atoms/admin-roles";
import {RequiredUserPermissionsSome} from "../../../atoms/admin-user-permissions/RequiredUserPermissionsSome";
import {
  MenuItem,
  MenuCategory,
  MenuContainer,
  MenuGroup,
  MenuGroupTitle,
} from "../../../atoms/menu";
import {TemporaryHideChart} from "../TemporaryHideChart";
import {
  getMenuDefinition,
  getMenuItemAccess,
  getMenuItemPermissions,
  getMenuItemSections,
  mapChecklistItemToMenuDefinition,
  mapDashboardsToMenuDefinition,
  MenuItem as ReportingMenuItem,
} from "./helpers";
import {ChecklistItem} from "../../../services/ReportingService";
import {QuicksightDashboard} from "../../../services/QuicksightService";
import {useScrollSpyContext} from "../../../hooks/useScrollSpy";

type Props = Readonly<{
  checklistItems?: ReadonlyArray<ChecklistItem>;
  dashboardItems?: ReadonlyArray<QuicksightDashboard>;
}>;

export const LOADING_PLACEHOLDER = "loading";

export const ReportingMenu = ({
  checklistItems = [],
  dashboardItems = [],
}: Props) => {
  const {chartId} = useParams<{chartId: string}>();
  const {activeId} = useScrollSpyContext();

  /**
   * Build helper structures containing dynamic checkbox items.
   */
  const {menu} = useMemo(() => {
    const checkboxMenu = mapChecklistItemToMenuDefinition(checklistItems);
    const dashboardMenu = mapDashboardsToMenuDefinition(dashboardItems);
    const menu = getMenuDefinition(checkboxMenu, dashboardMenu);

    return {menu};
  }, [checklistItems, dashboardItems]);

  return (
    <ReportingMenuColumn>
      {menu.map(category => {
        const {children: subCategories = []} = category;

        return (
          <MenuAccessGate key={category.urlComponent} menuItem={category}>
            <ReportingMenuContainer>
              <MenuCategory
                active
                iconName={category.icon}
                title={category.label}
              />
              {subCategories.length > 0 && (
                <CategoryItems>
                  {subCategories.map(subCategory => {
                    const {children: sections = []} = subCategory;

                    return (
                      <MenuAccessGate
                        key={subCategory.urlComponent}
                        menuItem={subCategory}
                      >
                        <MenuGroup>
                          <MenuGroupTitle>{subCategory.label}</MenuGroupTitle>
                          {sections.map(section => {
                            const isActive =
                              section.sectionItems?.includes(chartId) ||
                              activeId === section.urlComponent;

                            return (
                              <MenuAccessGate
                                key={section.urlComponent}
                                menuItem={section}
                              >
                                <MenuItemEllipsis active={isActive}>
                                  {section.urlComponent ===
                                  LOADING_PLACEHOLDER ? (
                                    section.label
                                  ) : (
                                    <NavHashLink
                                      smooth
                                      className={css`
                                        color: inherit;
                                        text-decoration: none;
                                        ${isActive &&
                                        `color: ${theme.color.primary};`}
                                      `}
                                      to={`/admin/${category.urlComponent}/${subCategory.urlComponent}#${section.urlComponent}`}
                                      title={section.label}
                                    >
                                      {section.label}
                                    </NavHashLink>
                                  )}
                                </MenuItemEllipsis>
                              </MenuAccessGate>
                            );
                          })}
                        </MenuGroup>
                      </MenuAccessGate>
                    );
                  })}
                </CategoryItems>
              )}
            </ReportingMenuContainer>
          </MenuAccessGate>
        );
      })}
      <AdminRoles
        roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
      >
        <ReportingMenuContainer>
          <PermissionsMenuSection />
        </ReportingMenuContainer>
      </AdminRoles>
    </ReportingMenuColumn>
  );
};

const MenuAccessGate = ({
  menuItem,
  children,
}: {
  menuItem: ReportingMenuItem;
  children: React.ReactNode;
}) => {
  const permissions = getMenuItemPermissions(menuItem);
  const access = getMenuItemAccess(menuItem, permissions);

  if (access === "hide") {
    return null;
  }

  if (access === "allow") {
    return <>{children}</>;
  }

  return (
    <RequiredUserPermissionsSome permissions={permissions}>
      <TemporaryHideChart chartIds={getMenuItemSections(menuItem)}>
        {children}
      </TemporaryHideChart>
    </RequiredUserPermissionsSome>
  );
};

const PermissionsMenuSection = () => {
  const location = useLocation();
  const isCategoryActive = location.pathname.startsWith("/admin/permissions");
  const isManagementActive = location.pathname.startsWith(
    "/admin/permissions/management",
  );

  return (
    <>
      <MenuCategory
        active={isCategoryActive}
        iconName="permissions"
        title="Permissions"
      />
      <CategoryItems>
        <MenuGroup>
          <MenuGroupTitle>General</MenuGroupTitle>
          <MenuItemEllipsis active={isManagementActive}>
            <NavLink
              className={({isActive}) =>
                css`
                  color: inherit;
                  text-decoration: none;
                  ${isActive && `color: ${theme.color.primary};`}
                `
              }
              to="/admin/permissions/management"
              title="Permissions Management"
            >
              Permissions Management
            </NavLink>
          </MenuItemEllipsis>
        </MenuGroup>
      </CategoryItems>
    </>
  );
};

const ReportingMenuColumn = styled(FlexColumn)`
  flex-grow: 1;
  flex-shrink: 0;
  margin-right: 20px;
  max-width: 320px;
  overflow: auto;
`;

const CategoryItems = styled.div`
  margin-top: 24px;
  margin-left: 32px;
`;

const ReportingMenuContainer = styled(MenuContainer)`
  width: 100%;
`;

const MenuItemEllipsis = styled(MenuItem)`
  a {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    max-height: calc((1ex + 0.5rem) * 2);
    line-height: calc(1ex + 0.5rem);
    width: 100%;
    text-overflow: ellipsis;
    word-break: break-all;
    overflow: hidden;
  }
`;
